import { Controller } from '@hotwired/stimulus'
import { TomOption } from 'tom-select/src/types/core.js'
import { TomSelectElement } from '../../types/common'

type SubmissionViewFieldOption = {
  type: string
  name: string
  multiple: string
  label: string
}

type SubmissionViewField = SubmissionViewFieldOption & {
  required: string
  hide_label: string
  subtitle: string
  order: string
}

type ChangePermissionTypeEvent = Event & {
  target: HTMLSelectElement
  params?: {
    mandatoryFields: Record<string, Omit<SubmissionViewFieldOption, 'name'>>
  }
}

export default class SubmissionViewFieldsController extends Controller {
  declare readonly fieldSelectorTarget: TomSelectElement
  declare readonly draggableContainerTarget: HTMLElement
  declare readonly submissionFieldPlaceholderTarget: HTMLElement
  declare readonly submissionMandatoryFieldPlaceholderTarget: HTMLElement
  declare readonly permissionTypeSelectorTarget: TomSelectElement

  declare readonly hasFieldSelectorTarget: boolean
  declare readonly hasDraggableContainerTarget: boolean
  declare readonly hasSubmissionFieldPlaceholderTarget: boolean
  declare readonly hasSubmissionMandatoryFieldPlaceholderTarget: boolean
  declare readonly hasPermissionTypeSelectorTarget: boolean

  static targets = [
    'fieldSelector',
    'draggableContainer',
    'submissionFieldPlaceholder',
    'submissionMandatoryFieldPlaceholder',
    'permissionTypeSelector',
  ]

  connect() {
    if (
      !this.hasFieldSelectorTarget ||
      !this.hasDraggableContainerTarget ||
      !this.hasSubmissionFieldPlaceholderTarget ||
      !this.hasSubmissionMandatoryFieldPlaceholderTarget ||
      !this.hasPermissionTypeSelectorTarget
    ) {
      throw Error('SubmissionViewFieldsController: Tried init without required targets')
    }
    // Initialize the Sortable component
    this.draggableContainerTarget.addEventListener('sortable:update', () => this.#handleSortChanged())
    setTimeout(() => {
      this.fieldSelectorTarget.tomselect.on('dropdown_open', this.#handleDisableSelections.bind(this))
      this.permissionTypeSelectorTarget.tomselect.on('change', this.#handleConfirmPermissionTypeChange.bind(this))
      this.#handleSortChanged()
    }) // Make sure TomSelect is initialized
  }

  disconnect() {
    this.draggableContainerTarget.removeEventListener('sortable:update', () => this.#handleSortChanged())
  }

  #handleConfirmPermissionTypeChange(newValue: string) {
    if (this.permissionTypeSelectorTarget.dataset.lastValue === newValue) {
      return
    } else if (
      !confirm(
        'Changing access level might disrupt your current configuration and changes could be lost.' +
          ' Do you want to continue?',
      )
    ) {
      this.permissionTypeSelectorTarget.tomselect.setValue(this.permissionTypeSelectorTarget.dataset.lastValue)
    } else {
      this.permissionTypeSelectorTarget.dataset.lastValue = newValue
      const selectedOption = this.permissionTypeSelectorTarget.selectedOptions[0]
      const newMandatoryFields = JSON.parse(selectedOption.dataset.submissionViewFieldsMandatoryFields) || []
      this.#handlePermissionTypeChanged(newMandatoryFields)
    }

    setTimeout(() => {
      this.permissionTypeSelectorTarget.tomselect.blur()
    }, 100)
  }

  add = this.#handleAdd.bind(this)
  remove = this.#handleRemove.bind(this)

  #handlePermissionTypeChanged(mandatoryFields: ChangePermissionTypeEvent['params']['mandatoryFields']) {
    // Re-enable selections that might've been disabled and are available now
    this.#toggleSelectionAll(false)
    this.#tagPreviousMandatoryFields()
    this.#handleMandatoryFields(mandatoryFields)
    this.#clearPreviousMandatoryFields(mandatoryFields)
    this.#handleSortChanged()
  }

  #clearPreviousMandatoryFields(newMandatoryFields: ChangePermissionTypeEvent['params']['mandatoryFields']) {
    const tagged = this.element.querySelectorAll<HTMLFieldSetElement>('.visible-submission-view-field.mandatory-tagged')
    tagged.forEach(el => {
      const fieldName = el.dataset.fieldName
      if (!Object.keys(newMandatoryFields).includes(fieldName)) {
        const required = Number(
          el.querySelector<HTMLInputElement>('input[name*=required]:not([type=hidden])')?.checked,
        ).toString()
        const subtitle = el.querySelector<HTMLInputElement>('textarea[name*=subtitle]')?.value || ''
        const order = el.querySelector<HTMLInputElement>('input[name*=order]')?.value || ''
        const type = el.querySelector<HTMLInputElement>('input[name*=type]')?.value || ''
        const multiple = el.querySelector<HTMLInputElement>('input[name*=multiple]')?.value || ''
        const label = el.querySelector<HTMLInputElement>('input[name*=label]')?.value || ''

        // Add as an additional field to preserve the users changes, if any
        const node = this.submissionFieldPlaceholderTarget.cloneNode(true) as HTMLFieldSetElement
        this.#appendSelection(node, {
          name: fieldName,
          required,
          order,
          subtitle,
          type,
          multiple,
          label,
        })
        node.querySelectorAll('input[name*=additional_placeholder]').forEach(child => {
          child.remove()
        })
      }
      el.remove()
    })
  }

  #tagPreviousMandatoryFields() {
    const visible = this.element.querySelectorAll('.visible-submission-view-field[data-field-tag*=mandatory]')
    visible.forEach(el => {
      el.classList.add('mandatory-tagged')
    })
  }

  #handleMandatoryFields(mandatoryFields: ChangePermissionTypeEvent['params']['mandatoryFields']) {
    // Keep track of new fields position so that we can offset them correctly when inserting the
    // new template
    let positionShift = 0
    Object.entries(mandatoryFields).forEach(([fieldName, fieldOptions]) => {
      const foundElement = this.element.querySelector<HTMLDivElement>(
        `.visible-submission-view-field[data-field-name="${fieldName}"]`,
      )
      if (!foundElement) ++positionShift

      const subtitle = foundElement?.querySelector<HTMLInputElement>('textarea[name*=subtitle]')?.value || ''
      const order = Number(foundElement?.querySelector<HTMLInputElement>('input[name*=order]')?.value || '0')

      // Clone the node from the hidden mock submission field on the page and cleanup
      const node = this.submissionMandatoryFieldPlaceholderTarget.cloneNode(true) as HTMLFieldSetElement
      // Make sure the tooltips have the right text
      this.#adjustTooltipTexts(node, 'Mandatory Placeholder', fieldOptions.label)

      this.#appendSelection(node, {
        name: fieldName,
        ...fieldOptions,
        ...((!foundElement
          ? {}
          : {
              order: (order + positionShift).toString(),
              subtitle,
            }) as SubmissionViewField),
      })
      // Remove placeholder inputs and old element
      node.querySelectorAll('input[name*=mandatory_placeholder]').forEach(child => {
        child.remove()
      })
      foundElement?.remove()
    })
  }

  // Controller's privates
  #handleAdd(selectEvent) {
    // Find the selected option from the tom select input on change
    const $option = selectEvent.target.selectedOptions[0]
    if (!$option) return

    // Start process of building the field selection on the form
    // Clone the node from the hidden mock submission field on the page
    const node = this.submissionFieldPlaceholderTarget.cloneNode(true) as HTMLFieldSetElement
    this.#appendSelection(node, {
      type: $option.dataset.inputType,
      label: $option.label,
      multiple: $option.dataset.inputMultiple ?? 'false',
      name: $option.value,
      required: '',
      hide_label: '',
      subtitle: '',
      order: '',
    })

    node.querySelectorAll('input[name*=additional_placeholder]').forEach(child => {
      child.remove()
    })
    // Clear the tom select input to prepare for next selection
    this.#clearFieldSelector()
  }

  #clearFieldSelector() {
    if (this.fieldSelectorTarget.value) {
      this.fieldSelectorTarget.tomselect.clear()
    }
  }

  #adjustTooltipTexts(node, placeholderValue, newValue) {
    node.querySelectorAll('[data-tooltip-text]').forEach((el: HTMLElement) => {
      el.dataset.tooltipText = el.dataset.tooltipText.replace(placeholderValue, newValue)
    })
  }

  #appendSelection(node: HTMLFieldSetElement, field: SubmissionViewField) {
    if (!field || !node) return
    // Remove the target tag from the cloned node to avoid conflict
    delete node.dataset.submissionViewFieldsTarget

    // Configure the node with active form fields
    this.#revealClonedNode(node)
    this.#setRowLabel(node, field)
    this.#setRowName(node, field)
    this.#addHiddenFields(node, field)
    this.#setRequiredField(node, field)
    this.#setHideLabelField(node, field)
    this.#updateTextarea(node, field)
    this.#toggleSelection(field.name, true)
    this.#setFieldOrder(node, field)

    // Add the configured node to the draggable list
    if (field.order) {
      this.draggableContainerTarget.insertBefore(node, this.draggableContainerTarget.children[Number(field.order)])
    } else {
      this.draggableContainerTarget.append(node)
    }

    // Let the extensions controller activate this field's (deferred) selector.
    this.dispatch('fieldAdded', { detail: { node } })
  }

  // Field Configuration beyond this point //
  #toggleSelection(fieldName: string, value: boolean) {
    if (typeof fieldName === 'undefined') return

    const foundOption = this.fieldSelectorTarget.tomselect.options[fieldName]
    this.#toggleOption(foundOption, value)
  }

  #toggleOption(option: TomOption, value: boolean) {
    if (typeof option.$div === 'undefined') return

    option.disabled = value
    option.$div.ariaDisabled = value

    if (value) {
      option.$div.removeAttribute('data-selectable')
    } else {
      option.$div.setAttribute('data-selectable', true)
    }
  }

  #toggleSelectionAll(value: boolean) {
    Object.values(this.fieldSelectorTarget.tomselect.options).forEach(option => {
      this.#toggleOption(option, value)
    })
  }

  removeSubmissionField(fieldName: string, item) {
    this.#toggleSelection(fieldName, false)
    item.currentTarget.closest('.submission-field-container').remove()

    this.#handleSortChanged()
  }

  createHiddenField(field: SubmissionViewField, name: string, value: string): HTMLInputElement {
    const hiddenField = document.createElement('input')
    hiddenField.setAttribute('type', 'hidden')
    hiddenField.setAttribute('name', this.#getInputName(field, name))
    hiddenField.setAttribute('value', value)
    return hiddenField
  }

  #addHiddenFields(node: HTMLElement, field: SubmissionViewField) {
    const { type, multiple, label } = field

    ;[
      this.createHiddenField(field, 'type', type),
      this.createHiddenField(field, 'multiple', multiple),
      this.createHiddenField(field, 'label', label),
    ].forEach(inputField => {
      if (inputField.name !== '') node.append(inputField)
    })
  }

  #setRequiredField(node: HTMLElement, field: SubmissionViewField) {
    const checkBox = node.querySelector<HTMLInputElement>('.required-check-box')
    const hiddenCheckBox = checkBox.previousSibling as HTMLInputElement
    const readOnlyValueCheckbox = checkBox.nextSibling as HTMLInputElement

    checkBox.name = this.#getInputName(field, 'required')
    hiddenCheckBox.name = this.#getInputName(field, 'required')
    if (readOnlyValueCheckbox) {
      readOnlyValueCheckbox.name = this.#getInputName(field, 'required')
    }
    checkBox.checked = field.required === '1'
  }

  #setHideLabelField(node: HTMLElement, field: SubmissionViewField) {
    const checkBox = node.querySelector<HTMLInputElement>('.hide-label-check-box')
    const hiddenCheckBox = checkBox.previousSibling as HTMLInputElement

    checkBox.name = this.#getInputName(field, 'hide_label')
    hiddenCheckBox.name = this.#getInputName(field, 'hide_label')
    checkBox.checked = field.hide_label === '1'
  }

  #updateTextarea(node: HTMLElement, field: SubmissionViewField) {
    const textarea = node.querySelector('textarea')
    textarea.name = this.#getInputName(field, 'subtitle')
    textarea.value = field.subtitle
  }

  #setRowLabel(node: HTMLElement, field: SubmissionViewField) {
    node.querySelector('.title').innerHTML = field.label
  }

  #setRowName(node: HTMLElement, field: SubmissionViewField) {
    node.dataset.fieldName = field.name
  }

  #revealClonedNode(node: HTMLFieldSetElement) {
    node.classList.add('visible-submission-view-field')
    node.classList.remove('hidden')
    node.disabled = false
  }

  #getInputName(field: SubmissionViewField, value: string) {
    return `submission_view[payload][${field.name}][${value}]`
  }

  #setFieldOrder(node: HTMLElement, field: SubmissionViewField) {
    const totalFieldCount = this.element.querySelectorAll('.visible-submission-view-field').length + 1
    const orderInput = document.createElement('input')

    orderInput.setAttribute('type', 'hidden')
    orderInput.setAttribute('name', this.#getInputName(field, 'order'))
    orderInput.setAttribute('value', totalFieldCount.toString())

    node.append(orderInput)
  }

  #handleSortChanged() {
    const fields = this.element.querySelectorAll<HTMLInputElement>('.visible-submission-view-field')

    fields.forEach((item, index) => {
      const name = item.dataset.fieldName
      const hiddenField = item.querySelector<HTMLInputElement>(`[name='submission_view[payload][${name}][order]']`)

      hiddenField.value = index.toString()
    })
  }

  #handleRemove(event) {
    this.removeSubmissionField(event.target.closest('fieldset').dataset.fieldName, event)
  }

  #handleDisableSelections() {
    const fields = this.element.querySelectorAll<HTMLDivElement>('.visible-submission-view-field')
    const select = this.fieldSelectorTarget.tomselect
    fields.forEach(field => {
      const name = field.dataset.fieldName
      const option = select.options[name]

      if (typeof option === 'undefined') return

      option.disabled = true
      option.$div.ariaDisabled = true
      option.$div.removeAttribute('data-selectable')
    })
  }
}
